
import { useRef, useEffect } from "react";
import Editor from "@monaco-editor/react";

interface CodeEditorProps {
  code: string;
  onChange: (value: string) => void;
  language?: string;
  height?: string;
  readOnly?: boolean;
}

const CodeEditor = ({
  code,
  onChange,
  language = "python",
  height = "240px",
  readOnly = false
}: CodeEditorProps) => {
  const editorRef = useRef<any>(null);
  
  const handleEditorDidMount = (editor: any) => {
    editorRef.current = editor;
    
    // Enhanced focus handling
    setTimeout(() => {
      if (editor) {
        editor.focus();
        // Position cursor at a good starting point if code exists
        const lineCount = editor.getModel()?.getLineCount() || 1;
        // Find the first line with "YOUR CODE HERE" comment
        const content = editor.getModel()?.getValue() || "";
        const lines = content.split('\n');
        let targetLine = 0;
        
        for (let i = 0; i < lines.length; i++) {
          if (lines[i].includes("YOUR CODE HERE")) {
            targetLine = i + 1; // Position right after the comment
            break;
          }
        }
        
        editor.setPosition({ 
          lineNumber: targetLine || Math.min(8, lineCount), 
          column: 5 
        });
        
        // Add a small visual indicator when editor gets focus
        editor.onDidFocusEditorWidget(() => {
          const element = editor.getDomNode();
          if (element) {
            element.classList.add('editor-focused');
            setTimeout(() => element.classList.remove('editor-focused'), 300);
          }
        });
      }
    }, 100);
  };

  return (
    <Editor
      height={height}
      language={language}
      value={code}
      theme="vs-dark"
      onChange={(value) => onChange(value || "")}
      options={{
        minimap: { enabled: false },
        fontSize: 14,
        scrollBeyondLastLine: false,
        wordWrap: "on",
        automaticLayout: true,
        lineNumbers: "on",
        folding: true,
        readOnly: readOnly,
        cursorBlinking: "smooth",
        smoothScrolling: true,
        cursorSmoothCaretAnimation: "on",
        tabSize: 4,
      }}
      className="rounded-md border border-slate-700 overflow-hidden transition-all duration-300"
      onMount={handleEditorDidMount}
    />
  );
};

export default CodeEditor;
